import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useHtClient } from '../providers/ht';

// Interactive-workbook exercise responses (ClickUp 86badfk6e).
// Backed by the public /exercises API (anonymous takers persist by quiz_id).

export interface ExerciseResponse {
  id: number;
  quiz_id: number;
  profile_id: string | null;
  personality: string;
  exercise_key: string;
  response: Record<string, any>;
  completed: boolean;
  created_at: string;
  updated_at: string;
}

export interface ExerciseResponseUpsert {
  personality: string;
  exercise_key: string;
  response?: Record<string, any>;
  completed?: boolean;
  // profile_id is NOT sent on the public save path (backend rejects it) — the
  // quiz→profile link is established at signup via a dedicated endpoint.
}

// Load every exercise response for a quiz (the taker's whole workbook + progress).
export const useExerciseResponses = (quizId: number | undefined) => {
  const { data: client } = useHtClient();

  return useQuery<ExerciseResponse[]>({
    queryKey: ['exerciseResponses', quizId],
    queryFn: async () => {
      if (!client) throw new Error('API client not initialized');
      const response = await client.get(`/exercises/quiz/${quizId}`);
      return response.data;
    },
    enabled: !!client && !!quizId,
    // Reading saved answers requires being logged in as the quiz's owner; an anonymous
    // taker gets 401. Don't retry that, and rely on the save cache below instead.
    retry: false,
    staleTime: 60 * 1000,
    gcTime: 30 * 60 * 1000,
  });
};

// Load exercise responses for a profile (return to saved answers after re-login).
export const useProfileExerciseResponses = (profileId: string | undefined) => {
  const { data: client } = useHtClient();

  return useQuery<ExerciseResponse[]>({
    queryKey: ['exerciseResponses', 'profile', profileId],
    queryFn: async () => {
      if (!client) throw new Error('API client not initialized');
      const response = await client.get(`/exercises/profile/${profileId}`);
      return response.data;
    },
    enabled: !!client && !!profileId,
    staleTime: 60 * 1000,
    gcTime: 30 * 60 * 1000,
  });
};

// Link a quiz's anonymous exercise responses to a profile at signup, so the
// user can return to answers entered before they created an account.
export const useAttachExercisesToProfile = () => {
  const { data: client } = useHtClient();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ quizId, profileId }: { quizId: number; profileId: string }) => {
      if (!client) throw new Error('API client not initialized');
      const response = await client.put(`/exercises/quiz/${quizId}/attach-profile`, {
        profile_id: profileId,
      });
      return response.data as { updated: number };
    },
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({ queryKey: ['exerciseResponses', variables.quizId] });
    },
  });
};

// Save or update a single exercise response (idempotent upsert by quiz + exercise_key).
export const useSaveExerciseResponse = () => {
  const { data: client } = useHtClient();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({
      quizId,
      ...body
    }: { quizId: number } & ExerciseResponseUpsert) => {
      if (!client) throw new Error('API client not initialized');
      const response = await client.put(`/exercises/quiz/${quizId}`, body);
      return response.data as ExerciseResponse;
    },
    onSuccess: (_data, variables) => {
      // Merge what was saved into the cache so the checkmark shows even for anonymous
      // takers: the server returns only {ok: true} to them and they cannot read
      // responses back. Built from the request, not the response, for that reason.
      const { quizId, ...saved } = variables;
      queryClient.setQueryData<ExerciseResponse[]>(
        ['exerciseResponses', quizId],
        (old) => {
          const prev = (old || []).find((r) => r.exercise_key === saved.exercise_key);
          const row = {
            ...(prev || {}),
            ...saved,
            quiz_id: quizId,
            response: saved.response ?? prev?.response ?? {},
            completed: saved.completed ?? prev?.completed ?? false,
          } as ExerciseResponse;
          return [...(old || []).filter((r) => r.exercise_key !== saved.exercise_key), row];
        }
      );
      queryClient.invalidateQueries({ queryKey: ['exerciseResponses', variables.quizId] });
    },
  });
};
